import { useState, useEffect } from 'react';
import { aiApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/Table';
import {
  Brain,
  AlertTriangle,
  DollarSign,
  Activity,
  User,
  Zap,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

interface AICostDaily {
  date: string;
  cost: number;
  tokens: number;
  calls: number;
}

interface AICostByModel {
  model: string;
  cost: number;
  tokens: number;
  calls: number;
}

interface AIAnomaly {
  id: string;
  type: string;
  userId?: string;
  username?: string;
  description: string;
  detectedAt: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
}

export function AIPage() {
  const [usageStats, setUsageStats] = useState<{
    totalCalls: number;
    totalTokens: number;
    totalCost: number;
    items: Array<{
      id: string;
      name: string;
      calls: number;
      tokens: number;
      cost: number;
    }>;
  } | null>(null);
  const [costByModel, setCostByModel] = useState<AICostByModel[]>([]);
  const [dailyCost, setDailyCost] = useState<AICostDaily[]>([]);
  const [anomalies, setAnomalies] = useState<AIAnomaly[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [groupBy, setGroupBy] = useState<'user' | 'team' | 'workflow' | 'model'>('user');


  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [usageRes, modelRes, dailyRes, anomalyRes] = await Promise.all([
        aiApi.getUsageStats({ groupBy }),
        aiApi.getCostByModel(),
        aiApi.getDailyCost(),
        aiApi.getAnomalies(),
      ]);

      if (usageRes.code === 0) setUsageStats(usageRes.data);
      if (modelRes.code === 0) setCostByModel(modelRes.data);
      if (dailyRes.code === 0) setDailyCost(dailyRes.data);
      if (anomalyRes.code === 0) setAnomalies(anomalyRes.data as AIAnomaly[]);
    } catch (err) {
      console.error('Failed to fetch AI data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [groupBy]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('zh-CN').format(num);
  };

  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <Badge variant="error">严重</Badge>;
      case 'high':
        return <Badge variant="error">高</Badge>;
      case 'medium':
        return <Badge variant="warning">中</Badge>;
      case 'low':
        return <Badge variant="info">低</Badge>;
      default:
        return <Badge>{severity}</Badge>;
    }
  };

  const COLORS = ['#4f46e5', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6'];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-zinc-900">AI 使用管理</h1>
        <p className="text-zinc-500 mt-1">监控 AI 使用情况和成本</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-zinc-500">总调用次数</p>
                <p className="text-2xl font-bold text-zinc-900 mt-1">
                  {usageStats ? formatNumber(usageStats.totalCalls) : '-'}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-xl">
                <Zap className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-zinc-500">总 Token 数</p>
                <p className="text-2xl font-bold text-zinc-900 mt-1">
                  {usageStats ? formatNumber(usageStats.totalTokens) : '-'}
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-xl">
                <Activity className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-zinc-500">总成本</p>
                <p className="text-2xl font-bold text-zinc-900 mt-1">
                  {usageStats ? formatCurrency(usageStats.totalCost) : '-'}
                </p>
              </div>
              <div className="p-3 bg-amber-100 rounded-xl">
                <DollarSign className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-zinc-500">异常告警</p>
                <p className="text-2xl font-bold text-zinc-900 mt-1">
                  {anomalies.length}
                </p>
              </div>
              <div className="p-3 bg-error-100 rounded-xl">
                <AlertTriangle className="w-6 h-6 text-error-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Cost Trend */}
        <Card>
          <CardHeader>
            <CardTitle>每日成本趋势</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dailyCost}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(value) => new Date(value).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' })}
                    stroke="#71717a"
                    fontSize={12}
                  />
                  <YAxis
                    stroke="#71717a"
                    fontSize={12}
                    tickFormatter={(value) => `$${value}`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e4e4e7',
                      borderRadius: '8px',
                    }}
                    formatter={(value: number) => [`$${value.toFixed(2)}`, '成本']}
                  />
                  <Line
                    type="monotone"
                    dataKey="cost"
                    stroke="#4f46e5"
                    strokeWidth={2}
                    dot={{ fill: '#4f46e5', strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Cost by Model */}
        <Card>
          <CardHeader>
            <CardTitle>模型成本分布</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={costByModel}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ model, percent }) => `${model} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="cost"
                  >
                    {costByModel.map((_entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e4e4e7',
                      borderRadius: '8px',
                    }}
                    formatter={(value: number) => [`$${value.toFixed(2)}`, '成本']}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Model Usage Details */}
      <Card>
        <CardHeader>
          <CardTitle>模型使用详情</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>模型</TableHead>
                <TableHead>调用次数</TableHead>
                <TableHead>Token 数</TableHead>
                <TableHead>成本</TableHead>
                <TableHead>占比</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {costByModel.map((model) => {
                const totalCost = costByModel.reduce((sum, m) => sum + m.cost, 0);
                const percentage = totalCost > 0 ? (model.cost / totalCost) * 100 : 0;
                return (
                  <TableRow key={model.model}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Brain className="w-4 h-4 text-zinc-400" />
                        <span className="font-medium">{model.model}</span>
                      </div>
                    </TableCell>
                    <TableCell>{formatNumber(model.calls)}</TableCell>
                    <TableCell>{formatNumber(model.tokens)}</TableCell>
                    <TableCell>{formatCurrency(model.cost)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-zinc-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-accent-600 rounded-full"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <span className="text-sm text-zinc-500">{percentage.toFixed(1)}%</span>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Usage Stats by User/Team/Workflow */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>使用统计</CardTitle>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value as typeof groupBy)}
              className="px-3 py-1.5 text-sm rounded-lg border border-zinc-300 bg-white"
            >
              <option value="user">按用户</option>
              <option value="team">按团队</option>
              <option value="workflow">按工作流</option>
              <option value="model">按模型</option>
            </select>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-600"></div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>名称</TableHead>
                  <TableHead>调用次数</TableHead>
                  <TableHead>Token 数</TableHead>
                  <TableHead>成本</TableHead>
                  <TableHead>平均成本/次</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {usageStats?.items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-zinc-400" />
                        <span className="font-medium">{item.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>{formatNumber(item.calls)}</TableCell>
                    <TableCell>{formatNumber(item.tokens)}</TableCell>
                    <TableCell>{formatCurrency(item.cost)}</TableCell>
                    <TableCell>{formatCurrency(item.calls > 0 ? item.cost / item.calls : 0)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Anomalies */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-error-600" />
            <CardTitle>异常检测</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>严重程度</TableHead>
                <TableHead>类型</TableHead>
                <TableHead>用户</TableHead>
                <TableHead>描述</TableHead>
                <TableHead>检测时间</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {anomalies.map((anomaly) => (
                <TableRow key={anomaly.id}>
                  <TableCell>{getSeverityBadge(anomaly.severity)}</TableCell>
                  <TableCell>
                    <span className="font-medium">{anomaly.type}</span>
                  </TableCell>
                  <TableCell>{anomaly.username || '-'}</TableCell>
                  <TableCell>{anomaly.description}</TableCell>
                  <TableCell>{new Date(anomaly.detectedAt).toLocaleString('zh-CN')}</TableCell>
                </TableRow>
              ))}
              {anomalies.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-zinc-500">
                    暂无异常检测记录
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
